// Pejalan kaki (warga) — HANYA berjalan di atas trotoar: mengikuti tepi blok dengan
// aturan "ikut tembok" (wall-following) di grid jalan. Strip trotoar = 0.16 tile dari
// tepi jalan yang tidak bertetangga jalan. Di tikungan dalam trotoar membelok di dalam
// tile; di sudut blok (jalan samping terbuka) warga membelok mengikuti blok atau
// menyeberang lurus ke trotoar seberang (zebra). Kendaraan darat: traffic.js.
// Kepadatan warga mengikuti jam kota (ramai siang, sepi tengah malam).
import { hashN } from "./sky";
import { paintBuildingAt } from "./paint";

const SIDE = 0.34; // offset tengah strip trotoar dari pusat tile
const left = (d) => [d[1], -d[0]];
const right = (d) => [-d[1], d[0]];
const add = (p, d, k = 1) => [p[0] + d[0] * k, p[1] + d[1] * k];
const dirOf = (d) => (d[0] > 0 ? "SE" : d[0] < 0 ? "NW" : d[1] > 0 ? "SW" : "NE");
// Faktor kepadatan warga per jam kota (0..24), interpolasi linear
const PED_PROFILE = [[0, 0.1], [5, 0.15], [7, 1], [12, 1], [17, 1], [20, 0.5], [22, 0.2], [24, 0.1]];
export function pedDensityAt(hour) {
  const h = ((hour % 24) + 24) % 24;
  for (let i = 1; i < PED_PROFILE.length; i++) {
    const [h0, v0] = PED_PROFILE[i - 1], [h1, v1] = PED_PROFILE[i];
    if (h <= h1) return v0 + (v1 - v0) * ((h - h0) / (h1 - h0));
  }
  return PED_PROFILE[PED_PROFILE.length - 1][1];
}

// c: { gridToScreen, cam, atmos, city, drawOccluded } — state engine bersama
export function createMovers(c) {
  let movers = [];
  let roadSet = new Set();
  let lastBalance = 0;
  const isRoad = (x, y) => roadSet.has(x + "," + y);
  const wallVec = (d, s) => (s === "L" ? left(d) : right(d));
  const center = (T) => [T[0] + 0.5, T[1] + 0.5];
  // titik "ujung strip" tile T untuk arah d & sisi tembok s (0.16 dari tepi depan)
  const stripEnd = (T, d, s) => add(add(center(T), wallVec(d, s), SIDE), d, SIDE);

  // Tentukan target berikutnya saat warga tiba di ujung strip (aturan ikut tembok)
  function decide(m) {
    const T = m.tile, d = m.d, s = m.s, wv = wallVec(d, s);
    const N = add(T, d);
    if (!isRoad(N[0], N[1])) { // tikungan dalam: belok menjauhi tembok, tetap di tile T
      m.d = s === "L" ? right(d) : left(d);
      m.target = stripEnd(T, m.d, s);
      return;
    }
    const NW = add(N, wv);
    if (!isRoad(NW[0], NW[1])) { // trotoar berlanjut lurus
      m.tile = N; m.target = stripEnd(N, d, s);
      return;
    }
    // Sudut blok: jalan samping terbuka di sisi tembok.
    // Opsi menyeberang lurus (zebra) bila trotoar berlanjut dalam ≤3 tile.
    const cross = hashN(m.seed * 7.7 + T[0] * 3.1 + T[1] * 5.3 + m.legs) < 0.4;
    if (cross) {
      let K = N;
      for (let i = 0; i < 3; i++) {
        K = add(K, d);
        if (!isRoad(K[0], K[1])) break;
        const KW = add(K, wv);
        if (!isRoad(KW[0], KW[1])) { m.tile = K; m.target = stripEnd(K, d, s); m.crossing = true; return; }
      }
    }
    // Belok mengikuti blok: ke titik sudut (0.16 masuk tile N), lalu susuri jalan samping
    m.pending = { tile: NW, d: wv, s };
    m.target = add(add(center(T), wv, SIDE), d, 1 - SIDE);
  }

  function spawn(m, seed) {
    const tiles = [...roadSet].map(k => k.split(",").map(Number));
    for (let k = 0; k < 80; k++) {
      const T = tiles[Math.floor(hashN(seed + k * 3.3) * tiles.length) % tiles.length];
      const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
      const d = dirs[Math.floor(hashN(seed + k * 7.7) * 4) % 4];
      const s = hashN(seed + k * 1.9) < 0.5 ? "L" : "R";
      const W = add(T, wallVec(d, s));
      if (isRoad(W[0], W[1])) continue; // sisi tembok harus bukan jalan (ada trotoar)
      m.tile = T; m.d = d; m.s = s; m.pending = null; m.crossing = false; m.legs = 0;
      m.pos = add(center(T), wallVec(d, s), SIDE);
      m.target = stripEnd(T, d, s);
      m.gx = m.pos[0]; m.gy = m.pos[1]; m.dir = dirOf(d);
      m.hidden = false; m.alpha = 0; m.fade = 1; // fade in
      return true;
    }
    return false;
  }

  function build(state, defs) {
    movers = [];
    roadSet = new Set(state.roads || []);
    if (!roadSet.size) return;
    const peopleDefs = (defs || []).filter(d => d.category === "Karakter" && d.paintCode);
    const pool = Math.min(16, Math.max(2, ((state.buildings || []).length || 1) * 2));
    const target = Math.max(1, Math.round(pool * pedDensityAt(c.city?.hour ?? 12)));
    for (let i = 0; i < pool; i++) {
      const def = peopleDefs.length ? peopleDefs[Math.floor(hashN(600 + i * 13) * peopleDefs.length) % peopleDefs.length] : null;
      const m = { kind: "walk", def, seed: 100 + i, speed: 0.14 + hashN(800 + i) * 0.12, hidden: true, alpha: 0, fade: 0, legs: 0, gx: 0, gy: 0, dir: "SE", parked: true };
      if (i < target && spawn(m, 500 + i * 19)) { m.alpha = 1; m.fade = 0; m.parked = false; }
      movers.push(m);
    }
    lastBalance = 0;
  }

  // Sesuaikan jumlah warga aktif dengan jam kota (fade out saat sepi, muncul saat ramai)
  function balance(t) {
    if (t - lastBalance < 2) return;
    lastBalance = t;
    const pool = movers.length;
    const target = Math.max(1, Math.round(pool * pedDensityAt(c.city?.hour ?? 12)));
    const active = movers.filter(m => !m.parked);
    if (active.length > target) {
      const m = active[Math.floor(hashN(t) * active.length) % active.length];
      if (!m.leaving) m.leaving = true;
    } else if (active.length < target) {
      const m = movers.find(x => x.parked);
      if (m && spawn(m, m.seed * 31 + t)) { m.parked = false; m.leaving = false; }
    }
  }

  function update(t, dt) {
    balance(t);
    for (const m of movers) {
      if (m.hidden) continue;
      if (m.fade > 0) { m.fade = Math.max(0, m.fade - dt / 0.8); m.alpha = 1 - m.fade; }
      if (m.leaving) { m.alpha -= dt / 0.8; if (m.alpha <= 0) { m.hidden = true; m.parked = true; m.leaving = false; continue; } }
      let step = m.speed * dt;
      while (step > 0) {
        const dx = m.target[0] - m.pos[0], dy = m.target[1] - m.pos[1];
        const dist = Math.hypot(dx, dy);
        if (dist <= step) {
          m.pos = m.target; step -= dist; m.legs++;
          if (m.pending) { m.tile = m.pending.tile; m.d = m.pending.d; m.s = m.pending.s; m.pending = null; m.target = stripEnd(m.tile, m.d, m.s); }
          else { m.crossing = false; decide(m); }
        } else {
          m.pos = [m.pos[0] + dx / dist * step, m.pos[1] + dy / dist * step];
          step = 0;
        }
      }
      const vx = m.target[0] - m.pos[0], vy = m.target[1] - m.pos[1];
      if (Math.abs(vx) + Math.abs(vy) > 1e-6) m.dir = dirOf([Math.abs(vx) >= Math.abs(vy) ? Math.sign(vx) : 0, Math.abs(vx) >= Math.abs(vy) ? 0 : Math.sign(vy)]);
      m.gx = m.pos[0]; m.gy = m.pos[1];
    }
  }

  // Warga fallback (bila aset katalog belum ada)
  function drawFallbackWalker(ctx, x, y, zoom, t, seed) {
    const z = (18 * zoom) / 40;
    ctx.fillStyle = "rgba(0,0,0,0.22)";
    ctx.beginPath(); ctx.ellipse(x, y + 2 * z, 6 * z, 2.6 * z, 0, 0, 7); ctx.fill();
    const bob = Math.sin(t * 6 + seed) * 1.2 * z;
    ctx.strokeStyle = "#1E293B"; ctx.lineWidth = Math.max(1, 1.4 * z);
    ctx.beginPath(); ctx.moveTo(x, y - 8 * z + bob); ctx.lineTo(x, y - 3 * z); ctx.stroke();
    ctx.fillStyle = "#FDBA74"; ctx.beginPath(); ctx.arc(x, y - 11 * z + bob, 3.2 * z, 0, 7); ctx.fill();
    ctx.fillStyle = "#3B82F6"; ctx.beginPath(); ctx.arc(x, y - 6 * z + bob, 3.8 * z, 0, 7); ctx.fill();
  }

  // ---- Cache sprite warga (semua zoom ≤ 1.6): paintCode karakter dieksekusi sekali per
  // (aset, arah, zoom-bucket, malam, fase langkah) lalu hanya di-blit — sebelumnya tiap warga
  // dilukis ulang tiap frame (penyebab utama frame berat di HP).
  const SPRITE_MAX_ZOOM = 1.6;
  const spriteCache = new Map();
  function getSprite(def, dir, zoom, night, t, seed) {
    const zb = Math.round(zoom * 20) / 20;
    const nb = night > 0.4 ? 1 : 0;
    const tb = Math.floor(((t + seed * 0.37) * 8) % 16); // 16 fase langkah @ 8 fps
    const key = `${def.bid}|${dir}|${zb}|${nb}|${tb}`;
    let sp = spriteCache.get(key);
    if (sp) return sp;
    if (spriteCache.size > 480) spriteCache.clear();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.ceil(48 * zb), cv = document.createElement("canvas");
    cv.width = cv.height = Math.max(1, Math.ceil(size * dpr));
    const g = cv.getContext("2d");
    g.scale(dpr, dpr);
    paintBuildingAt(g, { ...def, category: "Karakter" }, size / 2, size * 0.78, zb, { time: tb / 8 + seed * 0.37, night: nb ? 1 : 0, dir });
    sp = { cv, size };
    spriteCache.set(key, sp);
    return sp;
  }

  // Gambar satu warga (culling layar + oklusi mask bangunan di depannya)
  function drawOne(ctx, m, t) {
    if (m.hidden || m.alpha <= 0.01) return;
    const zoom = c.cam.zoom, pad = 40 * zoom;
    const [sx0, sy0] = c.gridToScreen(m.gx, m.gy);
    const W = ctx.canvas.clientWidth || ctx.canvas.width, H = ctx.canvas.clientHeight || ctx.canvas.height;
    if (sx0 < -pad || sx0 > W + pad || sy0 < -pad || sy0 > H + pad) return;
    const paint = (g, sx, sy) => {
      g.save();
      if (m.alpha < 1) g.globalAlpha = m.alpha;
      if (m.def && m.def.paintCode) {
        if (zoom <= SPRITE_MAX_ZOOM) {
          const sp = getSprite(m.def, m.dir, zoom, c.atmos.dark, t, m.seed);
          g.drawImage(sp.cv, sx - sp.size / 2, sy - sp.size * 0.78, sp.size, sp.size);
        } else {
          paintBuildingAt(g, { ...m.def, category: "Karakter" }, sx, sy, zoom, { time: t + m.seed * 0.37, night: c.atmos.dark, dir: m.dir });
        }
      } else drawFallbackWalker(g, sx, sy, zoom, t, m.seed);
      g.restore();
    };
    c.drawOccluded(m.gx, m.gy, 22 * zoom, paint);
  }

  return { build, update, drawOne, get list() { return movers; } };
}
